import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config";


export default function AttendeeSignup() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    const formData = Object.fromEntries(new FormData(e.target).entries());

    try {
      const res = await fetch(`${API_BASE_URL}/api/attendees`, {

        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to submit");
      }

      setSent(true);
      e.target.reset();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      console.error(err);
    }
  }

  if (sent) {
  return (
    <div className="max-w-2xl mx-auto mt-10 rounded-2xl border shadow-soft overflow-hidden bg-white">
      <div className="bg-brand-100 text-brand-800 px-6 py-5 border-b border-brand-200">
        <h1 className="text-xl font-semibold">Registration Submitted 🎉</h1>
        <p className="text-brand-700/90 text-sm">
          Thank you for registering for the Women & Girls in STEM Club.
        </p>
      </div>

      <div className="p-6">
        <p className="text-ink/80">
          We’ve received your details and will be in touch soon. You can return to the homepage or
          submit another registration.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/")}
            className="rounded-xl bg-brand-600 text-white px-4 py-2 font-medium hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-600"
          >
            Back to Home
          </button>
          <button
            onClick={() => {
              setSent(false);
              navigate(location.pathname, { replace: true });
            }}
            className="rounded-xl border border-brand-300 text-brand-700 px-4 py-2 font-medium hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-400"
          >
            Submit Another Registration
          </button>
        </div>
      </div>
    </div>
  );
}


  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl border shadow-soft mt-10">
      {/* Header*/}
      <header className="rounded-t-2xl bg-brand-100 border-b px-6 py-6 text-center">
        <h1 className="text-3xl font-bold text-ink">Join Our Free STEM After-School Club</h1>
        <p className="mt-2 text-ink/80 max-w-lg mx-auto">
          Parents/guardians: please complete the form below to register an attendee.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="p-6 grid gap-5" aria-describedby="attend-desc">
        <p id="attend-desc" className="sr-only">Attendee, guardian and contact details plus interests.</p>

        {error && (
          <div role="alert" className="rounded-lg border border-red-300 bg-red-50 text-red-700 p-3">{error}</div>
        )}

        <Field id="attendeeName" label="Attendee full name *">
          <input id="attendeeName" name="attendeeName" required className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <Field id="attendeeAge" label="Attendee age *" hint="Minimum age 6">
          <input id="attendeeAge" name="attendeeAge" type="number" min={6} required className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <Field id="guardianName" label="Parent / Guardian name *">
          <input id="guardianName" name="guardianName" required className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <Field id="guardianPhone" label="Parent / Guardian phone *">
          <input id="guardianPhone" name="guardianPhone" type="tel" inputMode="tel" required className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <Field id="guardianEmail" label="Parent / Guardian email *">
          <input id="guardianEmail" name="guardianEmail" type="email" required className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <fieldset className="grid gap-2" aria-labelledby="attendedBeforeLabel" role="radiogroup">
          <legend id="attendedBeforeLabel" className="text-sm font-medium">Has the attendee participated before?</legend>
          <div className="flex gap-4">
            {["Yes", "No"].map((opt) => (
              <label key={opt} className="inline-flex items-center gap-2 text-sm">
                <input type="radio" name="participatedBefore" value={opt} className="h-4 w-4" /> {opt}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="grid gap-2" aria-labelledby="interestLabel" role="radiogroup">
          <legend id="interestLabel" className="text-sm font-medium">Is the attendee interested in STEM activities?</legend>
          <div className="flex gap-4">
            {["Yes", "No", "Somewhat"].map((opt) => (
              <label key={opt} className="inline-flex items-center gap-2 text-sm">
                <input type="radio" name="interestInSTEM" value={opt} className="h-4 w-4" /> {opt}
              </label>
            ))}
          </div>
        </fieldset>

        <Field id="additionalInfo" label="Additional information (medical, accessibility needs, notes)">
          <textarea id="additionalInfo" name="additionalInfo" rows={4} className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <button
          type="submit"
          className="mt-2 rounded-xl bg-brand-600 text-white font-medium px-4 py-2 hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-600"
        >
          Submit registration
        </button>
      </form>
    </div>
  );
}

function Field({ id, label, hint, children }) {
  return (
    <div className="grid gap-1">
      <label htmlFor={id} className="text-sm font-medium">{label}</label>
      {hint && <p className="text-xs text-ink/60">{hint}</p>}
      {children}
    </div>
  );
}
